// API client to add and manage different api

import { BookingLink } from "../models/Membership";
import { SimulatorAssignmentResponse } from "../models/responseBodies/SimulatorAssignmentResponse";
import { AddBookingLinkRequest } from "../models/admin/AddBookingLinkRequest";

const getDriverApiBaseUrl = (): string => {
  const explicit = process.env.REACT_APP_WORLD_API_BASE_URL;
  if (!explicit) {
    throw new Error('Missing REACT_APP_WORLD_API_BASE_URL environment variable');
  }
  return explicit.endsWith('/') ? explicit : explicit + '/';
};

class AdminClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getDriverApiBaseUrl();
  }

  public async AddBookingLink(newLinkRequest: AddBookingLinkRequest): Promise<BookingLink> {
    const url = `${this.baseUrl}admin/bookinglinks`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(newLinkRequest)
    });
    if (!response.ok) {
      throw new Error(`Failed to add booking link (${response.status})`);
    }
    return await response.json() as BookingLink;
  }

  public async GetAllBookingLinks(): Promise<BookingLink[]> {
    const url = `${this.baseUrl}admin/bookinglinks`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to load booking links (${response.status})`);
    }
    return await response.json() as BookingLink[];
  }

  public async DeleteBookingLink(id: string): Promise<void> {
    const url = `${this.baseUrl}admin/bookinglinks/${encodeURIComponent(id)}`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'DELETE', headers });
    if (!response.ok) {
      throw new Error(`Failed to delete booking link (${response.status})`);
    }
  }

  public async GetMemberships(): Promise<Array<{ id: number; name: string }>> {
    const url = `${this.baseUrl}admin/memberships`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to load memberships (${response.status})`);
    }
    return await response.json() as Array<{ id: number; name: string }>;
  }

  public async GetLocations(): Promise<Array<{ id: number; name: string }>> {
    const url = `${this.baseUrl}admin/locations`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to load locations (${response.status})`);
    }
    return await response.json() as Array<{ id: number; name: string }>;
  }

  public async GetSimulatorAssignments(location: string): Promise<Array<SimulatorAssignmentResponse>> {
    const url = `${this.baseUrl}admin/simulator-assignments/${location}`;
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to load simualtor assignments (${response.status})`);
    }
    return await response.json() as Array<SimulatorAssignmentResponse>;
  }
}

const adminApi = new AdminClient();
export default adminApi;